/* eslint-disable */
import React, { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Table,
  TableContainer,
  TablePagination,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Button,
  TableSortLabel,
} from '@mui/material'
import KebabMenu from './components/kebabMenu'

// helpers
import { getStatus, getLastLogin } from '../../../../helpers/users'
import { sortUsers } from './usersList.helpers'

// styles
import styles from './usersList.module.scss'
import LockClosedIcon from '../../../../assets/LockClosed.svg'
import { useTranslation } from 'react-i18next'
const editStyle = {
  backgroundColor: '#eb1700',
  '&:hover': {
    backgroundColor: '#CC1400',
  },
  '&:disabled': {
    backgroundColor: '#665A47',
  },
  '&:focus': {
    backgroundColor: '#CC1400',
  },
}

const UsersList = ({ users, areUsersLoading }) => {
  const navigate = useNavigate()
  const [page, setPage] = React.useState(0)
  const [rowsPerPage, setRowsPerPage] = React.useState(10)
  const [sortBy, setSortBy] = useState('firstName')
  const [sortOrder, setSortOrder] = useState('asc')
  const {t} = useTranslation()

  React.useEffect(() => {
    setPage(0)
  }, [users])

  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value)
    setPage(0)
  }

  const handleSort = (property) => {
    const newOrder = sortBy === property && sortOrder === 'asc' ? 'desc' : 'asc'
    setSortBy(property)
    setSortOrder(newOrder)
  }

  const sortedUsers = useMemo(
    () => sortUsers({ users, sortBy, sortOrder }),
    [users, sortBy, sortOrder]
  )

  const renderTableHead = () => (
    <TableRow
      sx={{
        '& th': {
          backgroundColor: '#F5F5F5',
        },
      }}
    >
      <TableCell className={styles.userTableHeader}>
        <TableSortLabel className={styles.userTableHeaderLabel} />
      </TableCell>
      <TableCell className={styles.userTableHeader}>
        <TableSortLabel
          className={styles.userTableHeaderLabel}
          active={sortBy === 'firstName'}
          direction={sortOrder}
          onClick={() => handleSort('firstName')}
        >
           {t('first-name')}
          
        </TableSortLabel>
      </TableCell>
      <TableCell className={styles.userTableHeader}>
        <TableSortLabel
          className={styles.userTableHeaderLabel}
          active={sortBy === 'lastName'}
          direction={sortOrder}
          onClick={() => handleSort('lastName')}
        >
           {t('last-name')}
         
        </TableSortLabel>
      </TableCell>
      <TableCell className={styles.userTableHeader}>
        <TableSortLabel
          className={styles.userTableHeaderLabel}
          active={sortBy === 'email'}
          direction={sortOrder}
          onClick={() => handleSort('email')}
        >
           {t('email')}
         
        </TableSortLabel>
      </TableCell>
      <TableCell className={styles.userTableHeader}>
        <TableSortLabel
          className={styles.userTableHeaderLabel}
          active={sortBy === 'status'}
          direction={sortOrder}
          onClick={() => handleSort('status')}
        >
          {t('status')}
          
        </TableSortLabel>
      </TableCell>
      <TableCell className={styles.userTableHeader}> {t('last-login')}</TableCell>
      <TableCell className={styles.userTableHeader} />
      <TableCell className={styles.userTableHeader} />
    </TableRow>
  )

  const handleEditUser = (e, userId, navigate) => {
    e.stopPropagation()
    navigate(`/edit-user/${userId}`)
  }

  const renderTableBody = () => {
    const handleTableRowClick = (event, userId) => {
      event.stopPropagation()
      const nodeName = event.target?.nodeName
      if (nodeName?.toLowerCase() == 'td') {
        navigate(`/view-user/${userId}`)
      }
    }
    return sortedUsers
      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
      .map((user) => (
        <TableRow
          className={styles.tableBodyRow}
          key={user.id}
          onClick={(event) => handleTableRowClick(event, user.id)}
        >
          <TableCell sx={{ fontSize: '16px' }} className={styles.tableCell}>
            {getStatus(user) === 'LOCKED_OUT' && (
              <img
                src={LockClosedIcon}
                alt=""
                style={{ marginBottom: '4px', width: '24px', height: '24px' }}
              />
            )}
          </TableCell>
          <TableCell sx={{ fontSize: '16px' }} className={styles.tableCell}>
            {user.name.givenName}
          </TableCell>
          <TableCell sx={{ fontSize: '16px' }} className={styles.tableCell}>
            {user.name.familyName}
          </TableCell>
          <TableCell sx={{ fontSize: '16px' }} className={styles.tableCell}>
            {user.emails?.[0]?.value}
          </TableCell>
          <TableCell sx={{ fontSize: '16px' }} className={styles.tableCell}>
            {getStatus(user)}
          </TableCell>
          <TableCell sx={{ fontSize: '16px' }} className={styles.tableCell}>
            {getLastLogin(user)}
          </TableCell>
          <TableCell style={{marginRight: '30px'}}></TableCell>
          <TableCell sx={{fontSize: '16px' }}>
            <Button
              onClick={(e) => handleEditUser(e, user.id, navigate)}
              variant="contained"
              className="primary-btn"
              disableElevation
              sx={{ ...editStyle }}
            >
              {t('edit')}
            </Button>
          </TableCell>
        </TableRow>
      ))
  }

  const renderTable = () => (
    <div className={styles.container}>
      {(areUsersLoading || users.length > 0) && (
        <div>
          <p className={styles.results}>{users.length} {t('results')}</p>
          <TableContainer className={styles.tableContainer}>
            <Table stickyHeader className={styles.userTable}>
              <TableHead>{renderTableHead()}</TableHead>

              <TableBody>
                {areUsersLoading ? (
                  <TableRow>
                    <TableCell sx={{ border: 'none' }} colSpan={6}>
                      <div className={styles.loaderContainer}>
                        <div className={styles.loading}>{t('loading')}</div>
                        <CircularProgress
                          sx={{
                            color: 'black',
                          }}
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  renderTableBody()
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {users.length > 10 && (
            <TablePagination
              rowsPerPageOptions={[10, 25, 100]}
              component="div"
              count={users.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </div>
      )}
    </div>
  )

  return <>{renderTable()}</>
}

export default UsersList